import { ArrowLeftIcon } from '@heroicons/react/24/outline'
import cloneDeep from 'clone-deep'
import { LayoutLoading } from 'components/LayoutLoading'
import { IS_MAIN_COMPANY } from 'config'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { createTicket, getConfig } from 'services/apis/ticket'
import { Button, InputFile } from 'stories/components'
import { fileToBase64, InputConvert, InputValidate, toFileArray } from 'utils'
import { RenderInput } from 'utils/RenderInput'

import { defaultInputs } from './constants'

export const CreateTicket = () => {
  const navigate = useHistory()
  const auth = useSelector((state: any) => state.auth)
  const [inputs, setInputs] = useState(defaultInputs())
  const [isLoading, setLoading] = useState(false)
  const [files, setFiles] = useState<any>()
  const [customFields, setCustomFields] = useState<any>(null)

  useEffect(() => {
    if (IS_MAIN_COMPANY || !auth.isAuthenticated) navigate.push('/')

    const fetchConfig = async () => {
      setLoading(true)
      const result = await getConfig()
      if (result) {
        setCustomFields(result)
        let newInputs = cloneDeep(inputs)
        newInputs = { ...result, ...newInputs }
        setInputs(newInputs)
      }
      setLoading(false)
    }

    fetchConfig()
  }, [])

  const onChange = async (key: string, value: any) => {
    const newInputs = cloneDeep(inputs)

    const input = newInputs[key]
    input.value = InputConvert(input, value)
    input.error = ''

    setInputs(newInputs)
  }

  const onSubmit = async () => {
    const newInputs = cloneDeep(inputs)
    let values: Record<string, any> = {}
    let customFieldsJson: Record<string, any> = {}
    let hasError = false

    const configKeys = customFields ? Object.keys(customFields) : []

    Object.keys(inputs).forEach((key) => {
      if (inputs[key].visible === false) return null
      newInputs[key].error = InputValidate(newInputs[key])
      const { value, error } = newInputs[key]
      if (error) hasError = true
      if (configKeys.includes(key)) {
        customFieldsJson[key] = value || ''
      } else {
        values[key] = value || ''
      }
    })
    setInputs(newInputs)
    if (hasError) return false

    values['userName'] = auth.profile.name
    values['userId'] = auth.profile.id
    values['userEmail'] = auth.profile.email
    values['userPhone'] = auth.profile.phone ?? ''

    if (files) {
      const fileArray = toFileArray(files)
      const filesData = await Promise.all(fileArray.map(fileToBase64))
      values['files'] = filesData
    }

    if (Object.keys(customFieldsJson).length > 0) values['customFields'] = customFieldsJson

    setLoading(true)
    createTicket(values)
      .then(() => {
        setLoading(false)
        navigate.goBack()
      })
      .catch(() => setLoading(false))
  }

  const goBackToTickets = () => {
    navigate.goBack()
  }

  const onFileUpload = (value: any) => {
    setFiles(value || undefined)
  }

  return (
    <div className="py-6 px-2">
      <div className="relative shadow1 max-w-screen-2xl m-auto bg-white rounded p-3 md:p-7 pb-3 md:pb-3 sm:text-center lg:text-left w-full">
        <LayoutLoading show={isLoading} />

        <div className="gap-2 mb-3 flex items-center justify-between">
          <h1 className="text-2xl font-variation-settings-600 flex items-center gap-2" onClick={goBackToTickets}>
            <ArrowLeftIcon className="w-4 h-4" />
            <span>Submit a Ticket</span>
          </h1>
        </div>
        <div className="flex flex-col gap-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {Object.keys(inputs).map((key, index) => {
              const input = inputs[key]
              return (
                <div className={input.span ? `col-span-${input.span}` : ''} key={index}>
                  <RenderInput input={input} Key={key} onChange={onChange} />
                </div>
              )
            })}
          </div>
        </div>
        <p className="text-sm mt-2">*Provide detailed explanations with as many screenshots as possible for clarity.</p>
        <div className="mt-2">
          <InputFile
            multiple={true}
            onChange={onFileUpload}
            title="Files"
            acceptFileTypes="application/*,image/*"
            value={files}
          />
        </div>
        <div className="flex justify-end mt-6">
          <Button onClick={onSubmit}>Submit a Ticket</Button>
        </div>
      </div>
    </div>
  )
}
